import { type PropsWithChildren, startTransition, useRef } from 'react';
import cn from 'clsx';
import { cva } from 'class-variance-authority';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { initialPropertyTypeState } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';
import { type SearchState, SearchStoreProvider } from '@pkgs/components/hooks/stores/search/SearchStoreProvider';
import CrossIcon from '@pkgs/components/assets/icons/xsmall/Cross.svg';
import ArrowDownIcon from '@pkgs/components/assets/icons/xsmall/Arrow-Thin-Down.svg';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { ObjectTypeFilterDropdownActions } from './ObjectTypeFilterDropdownActions';
import { Popover, PopoverRoot, PopoverTrigger } from '../Popover/Popover';

import styles from './ObjectTypeFilterDropdown.module.css';

const labelCopyVariants = cva(styles.labelCopy, {
  variants: {
    aiSearchEnabled: {
      true: styles.labelCopyAi,
      false: null,
    },
  },
});

type ObjectTypeFilterDropdownProps = PropsWithChildren<{
  label: string;
  count?: number;
  className?: string;
  isActive?: boolean;
  onClose?: () => void;
  'data-testid'?: string;
  defaultState: Partial<SearchState>;
}>;

export const ObjectTypeFilterDropdown = ({
  label,
  count,
  children,
  isActive,
  className,
  defaultState,
  'data-testid': dataTestId,
}: ObjectTypeFilterDropdownProps) => {
  const { featureFlags } = useAppContext();
  const isAiSearchEnabled = Boolean(featureFlags.isAiSearchEnabled);
  const containerRef = useRef<HTMLDivElement>(null);
  const resetState = useSearchStore((state) => state.resetState);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const applyState = useSearchStore((state) => state.applyState);
  const setPromptValue = usePromptStore((state) => state.setPromptValue);

  const handleApplyClick = (state: SearchState) => {
    startTransition(() => {
      // reset property type state for non-selected object type categories
      const patch = {
        propertyTypeState: {
          ...initialPropertyTypeState,
          [state.objectTypeCategory]: state.propertyTypeState[state.objectTypeCategory],
        },
      };

      applyState({ state: { ...state, ...patch } });
      setPromptValue('');
    });
  };

  return (
    <PopoverRoot modal="trap-focus">
      <div ref={containerRef}>
        <PopoverTrigger
          render={
            <button className={cn(styles.button, className)} data-testid={dataTestId} data-active={isActive}>
              <Copy className={labelCopyVariants({ aiSearchEnabled: isAiSearchEnabled })} as="span" text={label} />
              {Boolean(count) && <Copy as="span" text={'+' + count} />}
              {isActive ? (
                <CrossIcon className={styles.crossIcon} onClick={() => startTransition(() => resetState(defaultState))} />
              ) : (
                <ArrowDownIcon className={styles.arrowDown} />
              )}
            </button>
          }
        />
        <Popover aria-label={label} portalContainer={containerRef} positionMethod="fixed">
          <SearchStoreProvider initialState={() => getStateSnapshot()} namespace="object-type-filter-dropdown">
            {children}
            <ObjectTypeFilterDropdownActions onApply={handleApplyClick} defaultState={defaultState} />
          </SearchStoreProvider>
        </Popover>
      </div>
    </PopoverRoot>
  );
};
